import {AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {Constants} from "../../util/constants";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {SiteUser, SiteUserService} from "../../services/site-user.service";
import {UtilService} from "../../services/util.service";
import {finalize} from "rxjs/operators";
import {MatTableDataSource} from "@angular/material/table";
import {timer} from "rxjs";
import {Router} from "@angular/router";
import {StateService} from "../../services/state.service";
import {saveAs} from 'file-saver'
import {PhonePipe} from "../../pipes/phone.pipe";
import {DateTime} from 'luxon';

@Component({
    templateUrl: './site-users.component.html',
    styleUrls: ['./site-users.component.css']
})
export class SiteUsersComponent implements OnInit, AfterViewInit {

    readonly columnsToDisplay = [
        'selected',
        'email', 'firstName', 'lastName', 'birthDate', 'created',
        'orderCount',
        'orderNumber', 'orderStatus', 'orderTotal', 'orderTime', 'address', 'phone'
    ];
    readonly INITIAL_PAGE_INDEX = 0;
    readonly INITIAL_PAGE_SIZE = Constants.DEFAULT_PAGE_SIZE;

    @ViewChild('paginator') paginator: MatPaginator;
    initializing: boolean = false;
    private users: SiteUser[] = [];
    dataSource: MatTableDataSource<SiteUser>;
    filter: string;
    showDisabled: boolean = false;
    selectedCount: number = 0;

    constructor(private siteUserService: SiteUserService,
                private cdRef: ChangeDetectorRef,
                private state: StateService,
                private router: Router,
                private utilService: UtilService) {
        this.showDisabled = !!this.state.get("siteShowDisabled");
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.initializing = true;
        this.cdRef.detectChanges();
        this.siteUserService.getAll().pipe(
            finalize(() => {
                this.initializing = false
                this.cdRef.detectChanges();
            })
        ).subscribe((out) => {
            this.users = out;
            this.dataSource = new MatTableDataSource<SiteUser>(this.visibleUsers);
            timer().subscribe(() => {
                this.dataSource.paginator = this.paginator;
            })
        }, this.utilService.getErrorHandler("Error retrieving site users"));
    }

    disabledChanged() {
        console.log("disabled changed");
        this.dataSource = new MatTableDataSource<SiteUser>(this.visibleUsers);
        this.dataSource.paginator = this.paginator;
        this.dataSource.filter = this.filter;
        this.state.set("siteShowDisabled", this.showDisabled);
    }

    updateSelection() {
        // Count how many users are selected
        let count = 0;
        for (let user of this.users)
            if (user.selected)
                ++count;
        this.selectedCount = count;
    }

    clearSelection() {
        for (let user of this.users)
            user.selected = false;
        this.selectedCount = 0;
    }

    get visibleUsers(): SiteUser[] {
        return this.showDisabled? this.users : this.users.filter((u) => !u.disabled);
    }

    applyFilter(ev) {
        this.dataSource.filter = this.filter;
        this.dataSource.paginator?.firstPage();
    }

    clearFilter() {
        this.filter = undefined;
        this.applyFilter(null);
    }

    pageChanged(ev: PageEvent) {
    }

    showUser(user: SiteUser) {
        this.state.set('currentUser', user);
        this.router.navigateByUrl('/site-user').then();
    }

    getShippingAddress(user: SiteUser): string {
        if (!user.street)
            return '';
        const street = user.street2? `${user.street}, ${user.street2}` : user.street;
        return [street, user.city, `${user.state} ${user.zip}`].join(", ")
    }

    private formatDate(isoDate: string): string {
        // Do this one manually to prevent unintended +-1 date shift due to UTC assumptions
        if (!isoDate)
            return "";
        const parts = isoDate.split("-");
        if (parts.length < 3)
            return "";
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }

    private formatTimestampToDate(isoTimestamp: string): string {
        return isoTimestamp? DateTime.fromSQL(isoTimestamp).toFormat("D") : '';
    }

    private formatPhone(phone: string): string {
        if (!phone)
            return phone;
        const phonePipe = new PhonePipe();
        return phonePipe.transform(phone);
    }

    private formatAddress(user: SiteUser): string {
        const address = this.getShippingAddress(user);
        return address? `"${address}"` : null;
    }

    export() {
        this.exportCommon(this.dataSource.filteredData);
    }

    exportSelected() {
        this.exportCommon(this.dataSource.data.filter((u) => u.selected));
    }

    private exportCommon(users: SiteUser[]) {
        // Build CSV from received data
        const headers = ["Email", "First Name", "Last Name", "Birth Date", "Added", "Order Count", "Order #", "Status",
            "Total", "Placed", "Shipping Address", "Phone"];
        let rows = [headers.join(",")];
        for (let user of users) {
            let row = [user.email, `"${user.firstName}"`, `"${user.lastName}"`,
                this.formatDate(user.birthDate), this.formatTimestampToDate(user.created), user.orderCount,
                user.orderNumber, user.orderStatus, user.orderTotal, this.formatTimestampToDate(user.orderTime),
                this.formatAddress(user), this.formatPhone(user.phone)];
            rows.push(row.join(","));
        }
        const contents = rows.join("\n");
        const blob = new Blob([contents], {type: "text/csv;charset=utf-8"});
        // Export to file
        saveAs(blob, "users.csv");
    }
}
